import { Box, Button, Grid, RadioGroup, Stack, Typography } from "@mui/material";
import { Drawer } from "@zfp-akademie-a-s/zfp-design";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { type ExpenseLine, ExpenseLineExpenseType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type FC, useContext, useRef, useState } from "react";
import { ConsumptionExpenseForm } from "../Forms/ConsumptionExpenseForm";
import { FinancialProductExpenseForm } from "../Forms/FinancialProductExpenseForm";
import { HousingExpenseForm } from "../Forms/HousingExpenseForm";
import { InsuranceExpenseForm } from "../Forms/InsuranceExpenseForm";
import { LifestyleExpenseForm } from "../Forms/LifestyleExpenseForm";
import { LoanExpenseForm } from "../Forms/LoanExpenseForm";
import { OtherExpenseForm } from "../Forms/OtherExpenseForm";
import { EXPENSE_TYPE_ORDER } from "../expenseTypes";
import { ExpenseTypeTile } from "./ExpenseTypeTile";

type Props = {
  onClose: () => void;
  expense: ExpenseLine;
  readonly open: boolean;
};

export type ExpenseFormHandle = {
  readonly submitForm: () => void;
  readonly deleteExpense: () => void;
};

export const EditExpenseDrawer: FC<Props> = ({ onClose, expense, open }) => {
  const { t } = useResource();
  const [expenseType, setExpenseType] = useState<ExpenseLineExpenseType>(expense.expenseType);
  const { readOnly } = useContext(AnalysisDetailContext);

  const housingExpenseFormRef = useRef<ExpenseFormHandle>(null);
  const consumptionExpenseFormRef = useRef<ExpenseFormHandle>(null);
  const lifestyleExpenseFormRef = useRef<ExpenseFormHandle>(null);
  const loanExpenseFormRef = useRef<ExpenseFormHandle>(null);
  const insuranceExpenseFormRef = useRef<ExpenseFormHandle>(null);
  const financialProductExpenseFormRef = useRef<ExpenseFormHandle>(null);
  const otherExpenseFormRef = useRef<ExpenseFormHandle>(null);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      closeLabel={t(Resources.Common.Close)}
      title={t(Resources.AnalysisDetail.Budget.Expenses.Form.Buttons.Edit)}
      startAction={
        <Button
          disabled={readOnly}
          variant="text"
          color="error"
          onClick={() => {
            if (expenseType === ExpenseLineExpenseType.housing) {
              housingExpenseFormRef.current?.deleteExpense();
            }
            if (expenseType === ExpenseLineExpenseType.consumption) {
              consumptionExpenseFormRef.current?.deleteExpense();
            }
            if (expenseType === ExpenseLineExpenseType.life_style) {
              lifestyleExpenseFormRef.current?.deleteExpense();
            }
            if (expenseType === ExpenseLineExpenseType.loan) {
              loanExpenseFormRef.current?.deleteExpense();
            }
            if (expenseType === ExpenseLineExpenseType.insurance) {
              insuranceExpenseFormRef.current?.deleteExpense();
            }
            if (expenseType === ExpenseLineExpenseType["financial-product"]) {
              financialProductExpenseFormRef.current?.deleteExpense();
            }
            if (expenseType === ExpenseLineExpenseType.other) {
              otherExpenseFormRef.current?.deleteExpense();
            }
          }}
        >
          {t(Resources.Common.Delete)}
        </Button>
      }
      primaryAction={
        <Button
          variant="contained"
          type="submit"
          disabled={readOnly}
          onClick={() => {
            if (expenseType === ExpenseLineExpenseType.housing) {
              housingExpenseFormRef.current?.submitForm();
            }
            if (expenseType === ExpenseLineExpenseType.consumption) {
              consumptionExpenseFormRef.current?.submitForm();
            }
            if (expenseType === ExpenseLineExpenseType.life_style) {
              lifestyleExpenseFormRef.current?.submitForm();
            }
            if (expenseType === ExpenseLineExpenseType.loan) {
              loanExpenseFormRef.current?.submitForm();
            }
            if (expenseType === ExpenseLineExpenseType.insurance) {
              insuranceExpenseFormRef.current?.submitForm();
            }
            if (expenseType === ExpenseLineExpenseType["financial-product"]) {
              financialProductExpenseFormRef.current?.submitForm();
            }
            if (expenseType === ExpenseLineExpenseType.other) {
              otherExpenseFormRef.current?.submitForm();
            }
          }}
        >
          {t(Resources.Common.Save)}
        </Button>
      }
    >
      <Box>
        <Box mb={Spacing.XXL}>
          <Stack spacing={Spacing.XL}>
            <Typography variant="subtitle1" fontWeight={600}>
              {t(Resources.AnalysisDetail.Budget.Expenses.ExpenseType)}
            </Typography>

            <RadioGroup
              aria-label={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseType)}
              value={expenseType}
              onChange={(_, value) => {
                setExpenseType(value as ExpenseLineExpenseType);
              }}
              sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: Spacing.M }}
            >
              {EXPENSE_TYPE_ORDER.map((value) => (
                <ExpenseTypeTile key={value} expenseType={value} disabled={value !== expenseType} />
              ))}
            </RadioGroup>
          </Stack>
        </Box>

        <Box mb={Spacing.XXL}>
          {expenseType === ExpenseLineExpenseType.housing && <HousingExpenseForm onClose={onClose} ref={housingExpenseFormRef} />}
          {expenseType === ExpenseLineExpenseType.consumption && (
            <ConsumptionExpenseForm onClose={onClose} ref={consumptionExpenseFormRef} />
          )}
          {expenseType === ExpenseLineExpenseType.life_style && <LifestyleExpenseForm onClose={onClose} ref={lifestyleExpenseFormRef} />}
          {expenseType === ExpenseLineExpenseType.loan && <LoanExpenseForm onClose={onClose} ref={loanExpenseFormRef} />}
          {expenseType === ExpenseLineExpenseType.insurance && <InsuranceExpenseForm onClose={onClose} ref={insuranceExpenseFormRef} />}
          {expenseType === ExpenseLineExpenseType["financial-product"] && (
            <FinancialProductExpenseForm onClose={onClose} ref={financialProductExpenseFormRef} />
          )}
          {expenseType === ExpenseLineExpenseType.other && <OtherExpenseForm onClose={onClose} ref={otherExpenseFormRef} />}
        </Box>
      </Box>
    </Drawer>
  );
};
